import type { BaseModel } from './baseModel';
import type { Message } from './message';
import type { ScheduledMessage } from './scheduledMessage';

// TODO: delete this
export type ClientStatus = 'open' | 'pending' | 'success-closed' | 'failed-closed';

interface InsurexData {
  loanProtectionInsurance?: {
    externalId?: string;
    simulationExternalId?: string;
  };
  unemploymentInsurance?: {
    externalId?: string;
    simulationExternalId?: string;
  };
}

interface CustomAttributes {
  [key: string]: string | number | boolean | InsurexData | undefined;
}

export interface ClientDraft {
  createdAt: Date;
  updatedAt: Date;
  firstName: string;
  lastName: string;
  email: string;
  rut: string | null;
  dni: string | null;
  isTestClient: boolean | null;
  isBlocked?: boolean | null;
  phone: string | null;
  savingsAmount: number | null;
  vehicleValue?: number | null;
  housingStatus?: string | null;
  educationalLevel?: string | null;
  degree?: string | null;
  companyName?: string | null;
  notes?: string | null;
  adminNotes?: string | null;
  workContract?: string | null;
  businessUnitId: number;
  motherLastName?: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  nationality: string | null;
  maritalStatus: string | null;
  address?: string | null;
  salary: number;
  salarySupplement?: number | null;
  totalSalary: number;
  score: number; // score given by the system (0-100), based on financialScore and interestScore
  financialScore: number | null;
  interestScore: number | null;
  manualScore: number | null; // used if we want to override the system score
  disabledAutomaticResponses: boolean | null;
  source: string | null;
  quote: string;
  lastMessageDate: Date;
  messagesCount: number;
  clientMessagesCount: number;
  imagesSent: number;
  followUpsSent: number;
  floidRequestsCount: number;
  floidRequestsDetails?: string;
  status?: ClientStatus | null;
  didBookVisit?: boolean;
  didVisit?: boolean;
  userScore?: number; // score given by the seller (0-10) to show how good of a prospect the client is
  webhookBodyAtCreation?: string | null;
  hubspotId?: number | null;
  hubspotDealId?: string | null;
  prospectMessage?: string | null;
  messages?: Message[];
  hasVehicleAsPartPayment: boolean;
  requiresFinancing: boolean;
  monthlyPaymentsAmount: number | null;
  budgetAmount: number | null;
  modelsOfInterest: string[] | null;
  modelsOfInterestIds: number[] | null;
  brandOfInterest?: string | null;
  hubspotComment?: string | null;
  disabledFollowUps?: boolean;
  hasValidName?: boolean;
  lastCRMUpdate?: Date | null;
  visitDate?: Date | null;
  isClosedConversation?: boolean;
  cost?: number | null;
  topics?: string[] | null;
  endReason?: string | null;
  branchOffice?: string | null;
  businessUnit?: {
    id: number;
    name: string;
    type: string;
    hasSellerManagement: boolean;
  };
  AIRevision?: string | null;
  hasValidatedAllData?: boolean;
  aiSafetyStatus?: string | null;
  creditSimulations?: unknown[];
  surveys?: {
    name: string;
    results: {
      question: string;
      response?: string | null;
    }[];
    status: 'pending' | 'finished' | 'aborted';
    updatedAt: Date;
  }[];
  creditStatus?: string | null;
  customAttributes?: CustomAttributes | null;
  userId?: number | null;
  user?: {
    id: number;
    name: string | null;
  };
  publicChatLink?: string | null;
  lastSentMessageRole?: string | null;
  unreadMessagesCount: number;
  clientType?: 'buy' | 'sell' | 'after-sale' | null;
  pilotAssignedUser?: string | null;
  lastLeadStatus?: string | null;
  hasInProcessTasks?: number;
}

export interface Client extends BaseModel, ClientDraft {
  scheduledMessages?: ScheduledMessage[];
}

export interface EditableClientAttributes {
  status?: ClientStatus | null;
  disabledAutomaticResponses?: boolean;
  userId?: number | null;
  notes?: string | null;
}

export interface EditableAdminClientAttributes extends EditableClientAttributes {
  adminNotes?: string;
  rut?: string;
  dni?: string;
  email?: string;
  phone?: number;
  firstName?: string;
  lastName?: string;
  salary?: number;
  salarySupplement?: number;
  savingsAmount?: number;
  vehicleValue?: number;
  housingStatus?: string;
  educationLevel?: string;
  degree?: string;
  workContract?: string;
  motherLastName?: string;
  gender?: string;
  dateOfBirth?: string;
  nationality?: string;
  maritalStatus?: string;
  source?: string;
  outboundCampaignId?: string;
  manualScore?: number;
  disabledAutomaticResponses?: boolean;
  disabledFollowUps?: boolean;
  isTestClient?: boolean;
  prospectMessage?: string;
  subProjectId?: number;
  hasVehicleAsPartPayment?: boolean;
  requiresFinancing?: boolean;
  monthlyPaymentsAmount?: number;
  budgetAmount?: number;
  modelsOfInterest?: string[];
  brandOfInterest?: string;
  hasValidName?: boolean;
  branchOffice?: string;
  aiSafetyStatus?: string;
}

export interface ClientWithLastMessage {
  id: number;
  lastName: string;
  firstName: string;
  phone: string;
  businessUnitName: string | null;
  messageId: number;
  messageText: string;
  messageStatus: string;
  createdAt: Date;
}

export interface FinancialAdvisorIndividualClientMetrics {
  hasOnboardingComplete: boolean;
  hasRefinanceRequest: boolean;
  hasOffers: boolean;
}

export const clientAttributes = [
  { title: 'nombre', value: 'client.firstName' },
  { title: 'apellido', value: 'client.lastName' },
  { title: 'sucursal', value: 'client.branchOffice' },
  { title: 'marca de interés', value: 'client.brandOfInterest' },
  { title: 'modelo de interés', value: 'client.modelsOfInterest' },
];
